import { useState } from 'react';
import { CalendarDays, FileText, Lock, PenLine, Plus, Trash2 } from 'lucide-react';
import {
  DAY_LOG_WEATHER,
  DAY_SIGNATURE_ROLES,
  DAY_WORKER_ROLES,
  signatureRoleLabel,
  weatherLabel,
  type PortalDayLog,
  type PortalDayWorker,
  type PortalDayWorkerInput,
  type SignAssemblyDayInput,
  type UpdatePortalDayWorkerInput,
} from '@proerp/shared';
import { Badge, Button, openFilePreview, Spinner } from '@proerp/ui';
import {
  useAddPortalDay,
  useAddPortalDayWorker,
  useRemovePortalDayWorker,
  useSignPortalDay,
  useUpdatePortalDay,
  useUpdatePortalDayWorker,
  usePortalDayLogs,
} from '@/lib/usePortalDayLogs';
import { DaySignatureDialog } from './DaySignatureDialog';

const dayFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'full', timeZone: 'Europe/Berlin' });
const shortDay = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeZone: 'Europe/Berlin' });

/** Heutiges Datum als ISO-Mitternacht in Ortszeit — der Tag, den man erfasst. */
function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())).toISOString();
}

/**
 * Welche Unterschrift noch fehlt. Der Tagesbericht wird von **beiden** Seiten
 * gegengezeichnet — Bauherr und Monteur, je Rolle genau einmal (der Server
 * erzwingt es über einen eindeutigen Schlüssel). Die Sperre nach der ersten
 * Unterschrift betrifft den Inhalt; die zweite Unterschrift bleibt möglich.
 */
function openSignatureRoles(day: PortalDayLog): readonly { value: string; label: string }[] {
  return DAY_SIGNATURE_ROLES.filter(
    (role) => !day.signatures.some((signature) => signature.role === role.value),
  );
}

/**
 * Bau-Tagesbericht im Partnerportal (ADR 0074 + ADR 0087, Stufe 3c).
 *
 * Der Fremdmonteur führt **denselben** Tagesbericht wie unsere Monteure und
 * lässt den Bauherrn auf seinem Gerät unterschreiben. Zwei Dinge sind hier
 * anders als in einem gewöhnlichen Formular:
 *
 * * **Auto-Save je Feld.** Auf der Baustelle wird man unterbrochen; ein
 *   „Speichern"-Knopf, den niemand drückt, verliert die Eingabe.
 * * **Nach der Unterschrift gesperrt.** Der Server erzwingt es, die Oberfläche
 *   zeigt es — ein Feld, das man tippen kann und das dann abgewiesen wird,
 *   wäre die schlechtere Auskunft.
 * * **Gesperrt heißt nicht fertig unterschrieben.** Bauherr UND Monteur
 *   zeichnen gegen (wie auf dem Papierformular); die Sperre betrifft den
 *   Inhalt, nicht die zweite Unterschrift.
 */
export function TaskDayLogs({ taskId, location }: { taskId: string; location: string | null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signingDayId, setSigningDayId] = useState<string | null>(null);

  const days = usePortalDayLogs(taskId, { enabled: isOpen });
  const addDay = useAddPortalDay(taskId);
  const updateDay = useUpdatePortalDay(taskId);
  const signDay = useSignPortalDay(taskId);
  const addWorker = useAddPortalDayWorker(taskId);
  const updateWorker = useUpdatePortalDayWorker(taskId);
  const removeWorker = useRemovePortalDayWorker(taskId);

  const report = days.data;
  const signingDay = report?.days.find((day) => day.id === signingDayId) ?? null;

  /**
   * Der vollständige Tagesbericht als PDF — im Fenster, nicht als Download
   * (ADR 0075). Auch der Unterschrift-Dialog zeigt ihn so: Wer gleich
   * gegenzeichnet, will vorher hineinsehen, nicht erst eine Datei suchen.
   */
  function openDayPdf(day: PortalDayLog): void {
    openFilePreview({
      path: `/assembly/${taskId}/day-logs/${day.id}/report.pdf`,
      title: `Bau-Tagesbericht ${shortDay.format(new Date(day.logDate))}`,
      filename: `Bautagesbericht_${shortDay.format(new Date(day.logDate))}.pdf`,
      mimeType: 'application/pdf',
    });
  }

  async function run<T>(action: () => Promise<T>): Promise<void> {
    setError(null);
    try {
      await action();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Das hat nicht geklappt.');
    }
  }

  return (
    <div className="mt-3 border-t border-border pt-3">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <CalendarDays className="size-4" aria-hidden />
        Bau-Tagesberichte
        <span aria-hidden>{isOpen ? '▾' : '▸'}</span>
      </button>

      {isOpen && (
        <div className="mt-3 flex flex-col gap-3">
          {days.isPending && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" /> Tagesberichte werden geladen …
            </p>
          )}
          {days.isError && (
            <p className="text-sm text-danger-600">Tagesberichte sind gerade nicht abrufbar.</p>
          )}

          {report?.days.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Noch kein Tag erfasst. Lege den ersten Bautag an — Witterung, Zeiten, Leistung,
              Behinderungen; unterschreiben lässt du ihn direkt hier.
            </p>
          )}

          {report?.days.map((day) => (
            <DayCard
              key={day.id}
              day={day}
              isEditable={report.isEditable}
              onPatch={(input) => void run(() => updateDay.mutateAsync({ dayId: day.id, input }))}
              onSign={() => setSigningDayId(day.id)}
              onOpenPdf={() => openDayPdf(day)}
              onAddWorker={(input) => void run(() => addWorker.mutateAsync({ dayId: day.id, input }))}
              onUpdateWorker={(workerId, input) =>
                void run(() => updateWorker.mutateAsync({ dayId: day.id, workerId, input }))
              }
              onRemoveWorker={(workerId) =>
                void run(() => removeWorker.mutateAsync({ dayId: day.id, workerId }))
              }
            />
          ))}

          {report?.isEditable && (
            <Button
              variant="secondary"
              type="button"
              isLoading={addDay.isPending}
              onClick={() => void run(() => addDay.mutateAsync({ logDate: todayIso() }))}
            >
              <Plus className="size-4" aria-hidden /> Bautag anlegen
            </Button>
          )}
          {report && !report.isEditable && (
            <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              Der Bericht zu diesem Einsatz ist abgeschlossen. Nachträge nimmt dein Ansprechpartner
              bei uns auf.
            </p>
          )}

          {error && (
            <p role="alert" className="text-sm text-danger-600">
              {error}
            </p>
          )}
        </div>
      )}

      {signingDay && (
        <DaySignatureDialog
          isOpen
          onClose={() => setSigningDayId(null)}
          day={signingDay}
          dayLabel={`${signingDay.number ? `Bericht Nr. ${signingDay.number} · ` : ''}${dayFormat.format(new Date(signingDay.logDate))}`}
          taskTitle={report?.taskTitle ?? ''}
          // Wer montiert hat, steht in der Personalliste — für die Unterschrift
          // „Bauleiter / Monteur" ist der erste Name der naheliegende Vorschlag.
          suggestedName={signingDay.workers[0]?.name}
          suggestedPlace={location ?? undefined}
          openRoles={openSignatureRoles(signingDay)}
          isPending={signDay.isPending}
          onOpenPdf={() => openDayPdf(signingDay)}
          onSign={(input: SignAssemblyDayInput) =>
            void run(async () => {
              await signDay.mutateAsync({ dayId: signingDay.id, input });
              setSigningDayId(null);
            })
          }
        />
      )}
    </div>
  );
}

function DayCard({
  day,
  isEditable,
  onPatch,
  onSign,
  onOpenPdf,
  onAddWorker,
  onUpdateWorker,
  onRemoveWorker,
}: {
  day: PortalDayLog;
  isEditable: boolean;
  onPatch: (input: Record<string, unknown>) => void;
  onSign: () => void;
  onOpenPdf: () => void;
  onAddWorker: (input: PortalDayWorkerInput) => void;
  onUpdateWorker: (workerId: string, input: UpdatePortalDayWorkerInput) => void;
  onRemoveWorker: (workerId: string) => void;
}) {
  // Gesperrt heißt gesperrt: Nach der Gegenzeichnung ist der Tag ein
  // Beweisdokument. Der Server lehnt Änderungen ab — die Maske sagt es vorher.
  const canEdit = isEditable && !day.lockedAt;
  /**
   * Unterschreiben ist NICHT dasselbe wie Bearbeiten (Fehlerbild 2026-08-10):
   * Der Knopf hing an `canEdit`, also verschwand er mit der ersten
   * Unterschrift — und die zweite Rolle kam nie dran, obwohl der Server sie
   * längst annahm. Maßgeblich ist, ob noch eine Rolle offen ist.
   */
  const openRoles = openSignatureRoles(day);
  const canSign = isEditable && openRoles.length > 0;

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">
          {day.number ? `Tag ${day.number} · ` : ''}
          {shortDay.format(new Date(day.logDate))}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {day.lockedAt && (
            <Badge tone={openRoles.length === 0 ? 'success' : 'warning'}>
              <Lock className="size-3" aria-hidden />
              {openRoles.length === 0 ? 'Unterschrieben' : 'Gesperrt · eine Unterschrift fehlt'}
            </Badge>
          )}
          <button
            type="button"
            onClick={onOpenPdf}
            className="inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FileText className="size-4" aria-hidden /> PDF
          </button>
          {canSign && (
            <button
              type="button"
              onClick={onSign}
              className="inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-sm text-accent-600 transition-colors hover:bg-muted"
            >
              <PenLine className="size-4" aria-hidden />
              {day.signatures.length === 0
                ? 'Unterschreiben'
                : `${openRoles[0].label} unterschreibt`}
            </button>
          )}
        </div>
      </div>

      {day.signatures.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          {day.signatures
            .map((signature) => `${signatureRoleLabel(signature.role)}: ${signature.signerName}`)
            .join(' · ')}
        </p>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field
          label="Witterung"
          value={day.weatherConditions.map((value) => weatherLabel(value) ?? value).join(', ')}
          canEdit={canEdit}
          render={() => (
            <div className="flex flex-wrap gap-1.5">
              {DAY_LOG_WEATHER.map((option) => {
                const isOn = day.weatherConditions.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      onPatch({
                        weatherConditions: isOn
                          ? day.weatherConditions.filter((value) => value !== option.value)
                          : [...day.weatherConditions, option.value],
                      })
                    }
                    className={`cursor-pointer rounded-full border px-2.5 py-1 text-xs transition-colors ${
                      isOn
                        ? 'border-[color:var(--chart-1)] bg-[color:var(--chart-1)]/10 text-foreground'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        />
        <NumberField label="Temperatur (°C)" value={day.temperatureC} canEdit={canEdit} onSave={(value) => onPatch({ temperatureC: value })} />
        <TimeField label="Arbeit von" value={day.workFrom} canEdit={canEdit} onSave={(value) => onPatch({ workFrom: value })} />
        <TimeField label="Arbeit bis" value={day.workTo} canEdit={canEdit} onSave={(value) => onPatch({ workTo: value })} />
        <NumberField label="Arbeitsstunden" value={day.workHours} canEdit={canEdit} onSave={(value) => onPatch({ workHours: value })} />
        <NumberField label="Reisezeit (h)" value={day.travelHours} canEdit={canEdit} onSave={(value) => onPatch({ travelHours: value })} />
      </div>

      <WorkerList
        day={day}
        canEdit={canEdit}
        onAdd={onAddWorker}
        onUpdate={onUpdateWorker}
        onRemove={onRemoveWorker}
      />

      <div className="mt-3 flex flex-col gap-3">
        <TextField label="Ausgeführte Arbeiten" value={day.work} canEdit={canEdit} onSave={(value) => onPatch({ work: value })} />
        <TextField
          label="Behinderungen / Erschwernisse"
          hint="Grundlage für Nachträge und Bauzeitverlängerung — hier gehört alles hinein, was aufgehalten hat."
          value={day.obstructions}
          canEdit={canEdit}
          onSave={(value) => onPatch({ obstructions: value })}
        />
        <TextField label="Besondere Vorkommnisse" value={day.specialEvents} canEdit={canEdit} onSave={(value) => onPatch({ specialEvents: value })} />
        <TextField label="Regieleistungen" value={day.extraWork} canEdit={canEdit} onSave={(value) => onPatch({ extraWork: value })} />
      </div>
    </div>
  );
}

/**
 * Wer an diesem Tag montiert hat — namentlich, nicht als Kopfzahl.
 *
 * Die Papiervorlage fragt „Anzahl der beschäftigten Arbeiter". Namentlich ist
 * es belastbar: Der Bauherr zeichnet die Aufstellung mit gegen, und im
 * Streitfall ist „drei Mann" keine Angabe, die jemand belegen kann. Aus den
 * Zeilen rechnet der Bericht außerdem die Personenstunden — sind sie erfasst,
 * gewinnen sie gegen die grobe Schnellerfassung.
 */
function WorkerList({
  day,
  canEdit,
  onAdd,
  onUpdate,
  onRemove,
}: {
  day: PortalDayLog;
  canEdit: boolean;
  onAdd: (input: PortalDayWorkerInput) => void;
  onUpdate: (workerId: string, input: UpdatePortalDayWorkerInput) => void;
  onRemove: (workerId: string) => void;
}) {
  const [newName, setNewName] = useState('');

  function add() {
    const name = newName.trim();
    if (!name) return;
    // Die Tagesstunden sind der beste erste Tipp für eine Person — wer
    // abweichend gearbeitet hat, korrigiert die eine Zeile.
    onAdd({ name, role: 'MONTEUR', hours: day.workHours ?? null });
    setNewName('');
  }

  return (
    <div className="mt-3">
      <span className="mb-1.5 block text-xs text-muted-foreground">Eingesetztes Personal</span>

      {day.workers.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {canEdit
            ? 'Noch niemand erfasst. Trag ein, wer heute montiert hat — die Namen stehen im Tagesbericht und werden mit unterschrieben.'
            : '—'}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {day.workers.map((worker) => (
            <WorkerRow
              key={worker.id}
              worker={worker}
              canEdit={canEdit}
              onUpdate={(input) => onUpdate(worker.id, input)}
              onRemove={() => onRemove(worker.id)}
            />
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            value={newName}
            placeholder="Weitere Person …"
            aria-label="Person hinzufügen"
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                add();
              }
            }}
            className="h-11 min-w-0 flex-1 rounded-lg border border-border bg-background px-2.5 text-sm text-foreground"
          />
          <Button variant="secondary" type="button" className="h-11" onClick={add} disabled={!newName.trim()}>
            <Plus className="size-4" aria-hidden /> Hinzufügen
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Eine Personalzeile. Auf dem Handy bekommt der Name die erste Zeile für sich,
 * Rolle, Stunden und Entfernen teilen sich die zweite — mit festen Breiten
 * rutscht der Mülleimer sonst allein auf eine dritte.
 */
function WorkerRow({
  worker,
  canEdit,
  onUpdate,
  onRemove,
}: {
  worker: PortalDayWorker;
  canEdit: boolean;
  onUpdate: (input: UpdatePortalDayWorkerInput) => void;
  onRemove: () => void;
}) {
  if (!canEdit) {
    return (
      <li className="text-sm text-foreground">
        {worker.name}
        <span className="text-muted-foreground">
          {' · '}
          {DAY_WORKER_ROLES.find((option) => option.value === worker.role)?.label ?? worker.role}
          {worker.hours != null && ` · ${String(worker.hours).replace('.', ',')} h`}
        </span>
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center gap-2">
      <input
        // `key` am Wert: Kommt die Antwort zurück, soll das Feld den
        // gespeicherten Stand zeigen und nicht den alten Entwurf behalten.
        key={`name:${worker.name}`}
        defaultValue={worker.name}
        aria-label={`Name ${worker.name}`}
        onBlur={(event) => {
          const next = event.target.value.trim();
          if (next && next !== worker.name) onUpdate({ name: next });
        }}
        className="h-11 w-full min-w-0 rounded-lg border border-border bg-background px-2.5 text-sm text-foreground sm:w-auto sm:flex-1"
      />
      <select
        value={worker.role}
        aria-label={`Rolle ${worker.name}`}
        onChange={(event) => onUpdate({ role: event.target.value })}
        className="h-11 min-w-32 flex-1 rounded-lg border border-border bg-background px-2 text-sm text-foreground sm:flex-none"
      >
        {DAY_WORKER_ROLES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <input
        key={`hours:${worker.hours ?? ''}`}
        inputMode="decimal"
        placeholder="Std."
        defaultValue={worker.hours != null ? String(worker.hours).replace('.', ',') : ''}
        aria-label={`Stunden ${worker.name}`}
        onBlur={(event) => {
          // Deutsche Eingabe: „8,5" ist der Normalfall auf der Baustelle.
          const raw = event.target.value.trim().replace(',', '.');
          const parsed = raw === '' ? null : Number(raw);
          if (parsed !== null && Number.isNaN(parsed)) return;
          if (parsed !== (worker.hours ?? null)) onUpdate({ hours: parsed });
        }}
        className="h-11 w-20 shrink-0 rounded-lg border border-border bg-background px-2.5 text-right text-sm tabular-nums text-foreground"
      />
      <button
        type="button"
        aria-label={`${worker.name} entfernen`}
        onClick={onRemove}
        className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-danger-600"
      >
        <Trash2 className="size-4" aria-hidden />
      </button>
    </li>
  );
}

function Field({
  label,
  value,
  canEdit,
  render,
}: {
  label: string;
  value: string;
  canEdit: boolean;
  render: () => React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {canEdit ? render() : <span className="text-sm text-foreground">{value || '—'}</span>}
    </div>
  );
}

/** Auto-Save beim Verlassen des Feldes — wie im internen Bericht. */
function TextField({
  label,
  hint,
  value,
  canEdit,
  onSave,
}: {
  label: string;
  hint?: string;
  value: string | null;
  canEdit: boolean;
  onSave: (value: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {hint && canEdit && <span className="text-xs text-muted-foreground/80">{hint}</span>}
      {canEdit ? (
        <textarea
          rows={2}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => draft !== (value ?? '') && onSave(draft.trim() || null)}
          className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
        />
      ) : (
        <span className="whitespace-pre-line text-sm text-foreground">{value || '—'}</span>
      )}
    </label>
  );
}

/** „8.5" ist keine deutsche Zahl — auch nicht im gesperrten Tag. */
function decimal(value: number | null): string {
  return value === null ? '' : String(value).replace('.', ',');
}

function NumberField({
  label,
  value,
  canEdit,
  onSave,
}: {
  label: string;
  value: number | null;
  canEdit: boolean;
  onSave: (value: number | null) => void;
}) {
  const [draft, setDraft] = useState(decimal(value));
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {canEdit ? (
        <input
          inputMode="decimal"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            // Deutsche Eingabe: „8,5" ist der Normalfall auf der Baustelle.
            const parsed = draft.trim() === '' ? null : Number(draft.replace(',', '.'));
            if (parsed !== null && Number.isNaN(parsed)) return;
            if (parsed !== value) onSave(parsed);
          }}
          // 44 px hoch (Apple HIG): Auf der Baustelle wird mit Handschuhen
          // getippt. Der Mobil-Audit misst genau das.
          className="h-11 rounded-lg border border-border bg-background px-2.5 text-sm text-foreground"
        />
      ) : (
        <span className="text-sm text-foreground">{decimal(value) || '—'}</span>
      )}
    </label>
  );
}

function TimeField({
  label,
  value,
  canEdit,
  onSave,
}: {
  label: string;
  value: string | null;
  canEdit: boolean;
  onSave: (value: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {canEdit ? (
        <input
          type="time"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => draft !== (value ?? '') && onSave(draft || null)}
          className="h-11 rounded-lg border border-border bg-background px-2.5 text-sm text-foreground"
        />
      ) : (
        <span className="text-sm text-foreground">{value || '—'}</span>
      )}
    </label>
  );
}
